import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { RotateCw } from 'lucide-react';
import { PLACE, placeBox, placeTurn, type Place } from '@/lib/float-scene.mjs';

// MOVING AND RESIZING AN OVERLAY ON THE PICTURE (owner 2026-10-24: "the overlay has a transparency bar, can be enlarged
// and moveable around the screen just like the app inshot does their overlays"). Laid over the big preview while one
// overlay is picked to place: drag anywhere to move it, pinch (two fingers) or pull a corner to resize it, the mouse
// wheel resizes too, arrow keys nudge it. Only an outline and four corner handles are drawn, at the edges of what shows,
// never over the picture's middle. What it changes is the overlay's place ({ x, y, s }, float-scene placeBox), the
// same numbers the file is made from. TURNED TOO (owner 2026-10-27: "the overlay ... isnt able to be rotated"): a round
// handle above it turns it, two fingers twist it, [ and ] turn it by 5 degrees (r, degrees clockwise). What to do is
// said off the picture, by the page (owner 2026-10-27: a block of instructions "placed on the image blocking out the
// view while your trying to edit it").

const NO_PLACE: Place = { x: 0, y: 0, s: 1 };

/** A place kept in range and rounded as the form sends it (spot: three decimals), so the preview is what is sent. */
function tidyPlace(p: Place): Place {
  const r = (v: number) => Math.round(v * 1000) / 1000;
  // Near straight snaps straight (under 2 degrees), so it is easy to put back.
  const turn = Math.round(placeTurn(p) * 10) / 10;
  return {
    x: r(Math.max(-PLACE.maxXY, Math.min(PLACE.maxXY, p.x))),
    y: r(Math.max(-PLACE.maxXY, Math.min(PLACE.maxXY, p.y))),
    s: r(Math.max(PLACE.minS, Math.min(PLACE.maxS, p.s))),
    ...(Math.abs(turn) >= 2 ? { r: turn } : {}),
  };
}
const angle = (dx: number, dy: number) => (Math.atan2(dy, dx) * 180) / Math.PI;

const pct = (v: number) => `${(v * 100).toFixed(3)}%`;

export function OverlayPlacer({ place, frame, label, hint, onChange, turn = false, turnLabel = '' }: {
  place: Place | null;
  frame: { w: number; h: number };
  label: string;
  hint: string;
  onChange: (p: Place) => void;
  /** It can be turned. */
  turn?: boolean;
  turnLabel?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const now = place || NO_PLACE;
  const nowRef = useRef(now);
  nowRef.current = now;
  // The fingers down (client px) and where the gesture started; a new finger starts the gesture again from here.
  const pts = useRef(new Map<number, { x: number; y: number }>());
  const start = useRef<{ place: Place; pts: Map<number, { x: number; y: number }>; corner: boolean; spin: boolean } | null>(null);
  // One change a frame: the page draws the overlay again on each.
  const pending = useRef<Place | null>(null);
  const raf = useRef(0);
  const send = (p: Place) => {
    pending.current = tidyPlace(p);
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      if (pending.current) onChange(pending.current);
      pending.current = null;
    });
  };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const restart = (corner: boolean, spin = false) => {
    start.current = { place: nowRef.current, pts: new Map(pts.current), corner, spin };
  };

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // The picture under it (compare, swipe to the next overlay) must not see this.
    e.stopPropagation();
    e.preventDefault();
    try {
      box.current?.setPointerCapture(e.pointerId);
    } catch {
      /* a pointer the browser no longer knows: it still moves the overlay while over the picture */
    }
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const el = e.target as HTMLElement;
    restart(pts.current.size === 1 && Boolean(el.closest('[data-corner]')), pts.current.size === 1 && turn && Boolean(el.closest('[data-spin]')));
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pts.current.has(e.pointerId) || !start.current || !box.current) return;
    e.stopPropagation();
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const r = box.current.getBoundingClientRect();
    const s0 = start.current;
    const ids = [...pts.current.keys()].filter((id) => s0.pts.has(id));
    if (ids.length >= 2) {
      // Pinch: the size by how far apart the fingers are, the middle between them carries it.
      const [a, b] = ids;
      const a0 = s0.pts.get(a)!; const b0 = s0.pts.get(b)!; const a1 = pts.current.get(a)!; const b1 = pts.current.get(b)!;
      const d0 = Math.hypot(a0.x - b0.x, a0.y - b0.y) || 1;
      const d1 = Math.hypot(a1.x - b1.x, a1.y - b1.y);
      // Twisting the two fingers turns it.
      const twist = turn ? angle(b1.x - a1.x, b1.y - a1.y) - angle(b0.x - a0.x, b0.y - a0.y) : 0;
      send({
        s: s0.place.s * (d1 / d0),
        x: s0.place.x + ((a1.x + b1.x - a0.x - b0.x) / 2) / r.width,
        y: s0.place.y + ((a1.y + b1.y - a0.y - b0.y) / 2) / r.height,
        ...(turn ? { r: placeTurn(s0.place) + twist } : {}),
      });
      return;
    }
    const p0 = s0.pts.get(e.pointerId);
    if (!p0) return;
    if (s0.spin) {
      // The turn handle: the overlay turns with the finger round its middle.
      const cx = r.left + (0.5 + s0.place.x) * r.width;
      const cy = r.top + (0.5 + s0.place.y) * r.height;
      send({ ...s0.place, r: placeTurn(s0.place) + angle(e.clientX - cx, e.clientY - cy) - angle(p0.x - cx, p0.y - cy) });
      return;
    }
    if (s0.corner) {
      // A corner: the size by how far the finger is from the overlay's middle.
      const cx = r.left + (0.5 + s0.place.x) * r.width;
      const cy = r.top + (0.5 + s0.place.y) * r.height;
      const d0 = Math.hypot(p0.x - cx, p0.y - cy) || 1;
      send({ ...s0.place, s: s0.place.s * (Math.hypot(e.clientX - cx, e.clientY - cy) / d0) });
      return;
    }
    send({ ...s0.place, x: s0.place.x + (e.clientX - p0.x) / r.width, y: s0.place.y + (e.clientY - p0.y) / r.height });
  };
  const onUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    pts.current.delete(e.pointerId);
    if (pts.current.size) restart(false);
    else start.current = null;
  };
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 0.05 : 0.01;
    const p = nowRef.current;
    const next: Record<string, Place> = {
      ArrowLeft: { ...p, x: p.x - step }, ArrowRight: { ...p, x: p.x + step }, ArrowUp: { ...p, y: p.y - step }, ArrowDown: { ...p, y: p.y + step },
      '+': { ...p, s: p.s * 1.05 }, '=': { ...p, s: p.s * 1.05 }, '-': { ...p, s: p.s / 1.05 },
      ...(turn ? { '[': { ...p, r: placeTurn(p) - 5 }, ']': { ...p, r: placeTurn(p) + 5 } } : {}),
    };
    if (!next[e.key]) return;
    e.preventDefault();
    onChange(tidyPlace(next[e.key]));
  };
  // The wheel resizes (not passive, so the page does not scroll under it).
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      send({ ...nowRef.current, s: nowRef.current.s * Math.exp(-e.deltaY * 0.0015) });
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const b = placeBox(now, frame);
  const l = b.left / frame.w;
  const t = b.top / frame.h;
  const w = b.w / frame.w;
  const h = b.h / frame.h;
  const deg = placeTurn(now);
  // The corners of what shows (an enlarged overlay's own corners are off the picture), kept just inside it; turned, its
  // own turned corners (owner 2026-11-01: "unable to resize the backdrop at all once you rotate it": they were only
  // drawn on the outline, nothing to take hold of).
  const vl = Math.max(0, l);
  const vt = Math.max(0, t);
  const vr = Math.min(1, l + w);
  const vb = Math.min(1, t + h);
  const turnedCorner = (sx: number, sy: number): readonly [number, number] => {
    const rad = (deg * Math.PI) / 180;
    const dx = (sx * b.w) / 2;
    const dy = (sy * b.h) / 2;
    return [l + w / 2 + (dx * Math.cos(rad) - dy * Math.sin(rad)) / frame.w, t + h / 2 + (dx * Math.sin(rad) + dy * Math.cos(rad)) / frame.h];
  };
  const corners = deg ? [turnedCorner(-1, -1), turnedCorner(1, -1), turnedCorner(-1, 1), turnedCorner(1, 1)]
    : vr - vl > 0.02 && vb - vt > 0.02 ? ([[vl, vt], [vr, vt], [vl, vb], [vr, vb]] as const) : [];
  // The turn handle: above the overlay's top middle along its own up (straight: over what shows), kept on the picture.
  const spinAt = (() => {
    if (!deg) return [Math.max(0.06, Math.min(0.94, (vl + vr) / 2)), Math.max(0.04, Math.min(0.96, vt + 0.04))];
    const rad = (deg * Math.PI) / 180;
    const up = (h / 2) * frame.h * 0.82;
    return [Math.max(0.06, Math.min(0.94, l + w / 2 + (Math.sin(rad) * up) / frame.w)), Math.max(0.04, Math.min(0.96, t + h / 2 - (Math.cos(rad) * up) / frame.h))];
  })();

  return (
    <div
      ref={box}
      tabIndex={0}
      role="application"
      aria-label={`${label}. ${hint}`}
      title={hint}
      className="absolute inset-0 z-20 cursor-move touch-none select-none overflow-hidden rounded-2xl outline-none"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onKeyDown={onKey}
      data-testid="qt-overlay-placer"
      data-place={`${now.x},${now.y},${now.s}${deg ? `,${deg}` : ''}`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute rounded-[3px] border-[1.5px] border-dashed border-white/85 shadow-[0_0_0_1px_rgba(0,0,0,0.45)]"
        style={{ left: pct(l), top: pct(t), width: pct(w), height: pct(h), ...(deg ? { transform: `rotate(${deg}deg)` } : {}) }}
        data-testid="qt-overlay-outline"
      />
      {turn && (
        <span
          data-spin="1"
          aria-hidden
          title={turnLabel}
          className="absolute z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-grab items-center justify-center active:cursor-grabbing"
          style={{ left: pct(spinAt[0]), top: pct(spinAt[1]) }}
          data-testid="qt-overlay-turn"
        >
          <i data-spin="1" className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-primary text-primary-foreground shadow-[0_0_0_1px_rgba(0,0,0,0.5)]">
            <RotateCw className="pointer-events-none h-4 w-4" aria-hidden />
          </i>
        </span>
      )}
      {corners.map(([x, y], i) => (
        <span
          key={i}
          data-corner="1"
          aria-hidden
          className="absolute flex h-11 w-11 cursor-nwse-resize items-center justify-center"
          style={{
            left: `clamp(0px, calc(${pct(x)} - 22px), calc(100% - 44px))`,
            top: `clamp(0px, calc(${pct(y)} - 22px), calc(100% - 44px))`,
          }}
          data-testid="qt-overlay-corner"
        >
          <i data-corner="1" className="block h-4 w-4 rounded-full border-2 border-white bg-primary shadow-[0_0_0_1px_rgba(0,0,0,0.5)]" />
        </span>
      ))}
    </div>
  );
}
